import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Save, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { getConnectorConfig, saveConnectorConfigs } from '../../utils/connectorConfig';

function ActionProposal({ msg, index, isLight, onConfirm, onCancel }) {
  const { actionProposal, actionStatus, actionError } = msg;
  if (!actionProposal) return null;

  if (actionStatus === 'done') {
    return (
      <div className={`text-xs px-2.5 py-1.5 rounded-lg ${isLight ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-green-900/20 text-green-400 border border-green-800'}`}>
        ✓ Created successfully.
      </div>
    );
  }
  if (actionStatus === 'error') {
    return (
      <div className={`text-xs px-2.5 py-1.5 rounded-lg ${isLight ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-red-900/20 text-red-400 border border-red-800'}`}>
        ✕ {actionError || 'Failed to create.'}
      </div>
    );
  }
  if (actionStatus === 'cancelled') {
    return (
      <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Cancelled.</p>
    );
  }

  const isPending = actionStatus === 'pending';
  return (
    <div className="flex gap-1.5">
      <button
        onClick={() => onConfirm(index)}
        disabled={isPending}
        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
          isLight ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
        } disabled:opacity-60`}>
        {isPending ? <Loader2 size={11} className="animate-spin" /> : null}
        Confirm
      </button>
      <button
        onClick={() => onCancel(index)}
        disabled={isPending}
        className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors disabled:opacity-60 ${
          isLight ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
        }`}>
        Cancel
      </button>
    </div>
  );
}

function Message({ msg, index, isLight, onNavigate, onConfirmAction, onCancelAction }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isLight ? 'bg-blue-500' : 'bg-emerald-600'}`}>
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-white" />}
      </div>
      <div className="max-w-[88%] flex flex-col gap-1.5">
        <div className={`rounded-xl px-3 py-2 text-sm leading-relaxed overflow-x-auto text-white ${isLight ? 'bg-blue-500' : 'bg-emerald-600'} ${
          isUser ? 'rounded-tr-none chatbot-user-bubble' : 'rounded-tl-none chatbot-assistant-bubble'
        }`}>
          {isUser ? msg.content : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-1 space-y-0.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-1 space-y-0.5">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                a: ({ children, href }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:opacity-80 break-all">
                    {children}
                  </a>
                ),
                code: ({ inline, children }) => inline
                  ? <code className="bg-black/20 rounded px-1 text-xs font-mono">{children}</code>
                  : <pre className="bg-black/30 rounded p-2 text-xs font-mono overflow-x-auto mt-1 whitespace-pre-wrap break-all"><code>{children}</code></pre>,
                table: ({ children }) => <div className="overflow-x-auto"><table className="text-xs border-collapse w-full mt-1">{children}</table></div>,
                th: ({ children }) => <th className="border border-gray-500 px-2 py-1 text-left bg-black/20">{children}</th>,
                td: ({ children }) => <td className="border border-gray-500 px-2 py-1">{children}</td>,
              }}
            >
              {msg.content}
            </ReactMarkdown>
          )}
        </div>
        {/* Action buttons */}
        {msg.actions?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-1">
            {msg.actions.map((action, i) => (
              <button key={i} onClick={() => onNavigate(action.path)}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  isLight ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}>
                {action.label}<ArrowRight size={11} />
              </button>
            ))}
          </div>
        )}
        {msg.actionProposal && (
          <div className="pl-1">
            <ActionProposal msg={msg} index={index} isLight={isLight} onConfirm={onConfirmAction} onCancel={onCancelAction} />
          </div>
        )}
      </div>
    </div>
  );
}

const PAGE_ACTIONS = [
  { keywords: ['catalog', 'browse', 'negotiate', 'transfer data', 'consumer dashboard'], roles: ['consumer'], label: 'Open Consumer Dashboard', path: '/consumer-dashboard' },
  { keywords: ['asset', 'create asset', 'publish data', 'data source'], roles: ['provider'], label: 'Open Assets', path: '/assets' },
  { keywords: ['policy', 'policies', 'odrl', 'access restriction'], roles: ['provider'], label: 'Open Policies', path: '/policies' },
  { keywords: ['data offer', 'contract definition', 'publish offer'], roles: ['provider'], label: 'Open Data Offers', path: '/provider-data-exchange' },
  { keywords: ['dashboard', 'connector configuration', 'configure connector', 'overview'], roles: ['both'], label: 'Open Dashboard', path: '/dashboard' },
];

function getPageActions(userMessage, isProvider, isConsumer) {
  const msg = userMessage.toLowerCase();
  const actions = [];
  for (const pa of PAGE_ACTIONS) {
    if (!pa.keywords.some(k => msg.includes(k))) continue;
    const roleMatch = pa.roles.includes('both') ||
      (pa.roles.includes('provider') && isProvider) ||
      (pa.roles.includes('consumer') && isConsumer);
    if (roleMatch) actions.push({ label: pa.label, path: pa.path });
  }
  // Deduplicate by path
  return actions.filter((a, i, arr) => arr.findIndex(b => b.path === a.path) === i);
}

function ConfigFormMessage({ isLight, showProvider, showConsumer, showApiKey, onSave }) {
  const [providerUrl, setProviderUrl] = useState(
    showProvider ? (getConnectorConfig('providerEdcUrl') || getConnectorConfig('provider_url') || '') : ''
  );
  const [consumerUrl, setConsumerUrl] = useState(
    showConsumer ? (getConnectorConfig('consumerEdcUrl') || getConnectorConfig('consumer_url') || '') : ''
  );
  const [apiKey, setApiKey] = useState(
    showApiKey ? (getConnectorConfig('providerApiKey') || getConnectorConfig('edc_api_key') || '') : ''
  );
  const [saved, setSaved] = useState(false);

  const inputClass = `w-full px-3 py-1.5 text-xs rounded-lg border outline-none ${
    isLight
      ? 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
      : 'bg-gray-800 border-gray-600 text-gray-100 focus:border-emerald-500'
  }`;

  const canSave = (showProvider && providerUrl) || (showConsumer && consumerUrl) || (showApiKey && apiKey && !showProvider && !showConsumer);

  const handleSave = () => {
    onSave({ providerUrl: showProvider ? providerUrl : '', consumerUrl: showConsumer ? consumerUrl : '', apiKey: showApiKey ? apiKey : '' });
    setSaved(true);
  };

  if (saved) {
    return (
      <div className={`rounded-xl px-3 py-2 text-sm ${isLight ? 'bg-gray-200 text-gray-800' : 'bg-gray-700 text-gray-100'}`}>
        ✓ Configuration saved successfully!
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-3 text-sm rounded-tl-none w-full ${isLight ? 'bg-gray-200 text-gray-800' : 'bg-gray-700 text-gray-100'}`}>
      <p className="font-medium mb-3">Configure your EDC connector:</p>
      <div className="space-y-3">
        {showProvider && (
          <div>
            <label className={`text-xs mb-1 block ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Provider Management API URL</label>
            <input
              type="text"
              value={providerUrl}
              onChange={e => setProviderUrl(e.target.value)}
              className={inputClass}
            />
          </div>
        )}
        {showConsumer && (
          <div>
            <label className={`text-xs mb-1 block ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Consumer Management API URL</label>
            <input
              type="text"
              value={consumerUrl}
              onChange={e => setConsumerUrl(e.target.value)}
              className={inputClass}
            />
          </div>
        )}
        {showApiKey && (
          <div>
            <label className={`text-xs mb-1 block ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>API Key <span className="opacity-60">(optional)</span></label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              className={inputClass}
            />
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={!canSave}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            canSave
              ? isLight ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
              : isLight ? 'bg-gray-300 text-gray-400 cursor-not-allowed' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Save size={12} />Save Configuration
        </button>
      </div>
    </div>
  );
}

const SUGGESTIONS_PROVIDER = [
  'How do I create an asset?',
  'How do I create a policy?',
  'How do I publish a data offer?',
  'How do I view my contracts?',
];

const SUGGESTIONS_CONSUMER = [
  'How do I browse a catalog?',
  'How do I negotiate a contract?',
  'How do I transfer data?',
  'Why is my negotiation stuck?',
];

const SUGGESTIONS_BOTH = [
  'How do I create an asset?',
  'How do I browse a catalog?',
  'How do I negotiate a contract?',
  'How do I create a policy?',
];

function getWelcomeMessage(isProvider, isConsumer) {
  if (isProvider && isConsumer) return "Hi! I'm the ENACT Assistant. How can I help you today?";
  if (isProvider) return "Hi! I'm the ENACT Assistant. I can help you manage your assets, policies, and data offers.";
  if (isConsumer) return "Hi! I'm the ENACT Assistant. I can help you browse catalogs, negotiate contracts, and transfer data.";
  return "Hi! I'm the ENACT Assistant. How can I help you?";
}

function getSuggestions(isProvider, isConsumer) {
  if (isProvider && isConsumer) return SUGGESTIONS_BOTH;
  if (isProvider) return SUGGESTIONS_PROVIDER;
  if (isConsumer) return SUGGESTIONS_CONSUMER;
  return SUGGESTIONS_BOTH;
}

const CONFIG_INTENT_KEYWORDS = ['configure', 'set up', 'setup', 'my connector', 'provider url', 'consumer url', 'management url', 'management api', 'connect my', 'add my connector', 'set my url', 'change my url', 'update my url', 'change the url', 'update the url', 'api key'];

function isConfigIntent(message) {
  const msg = message.toLowerCase();
  return CONFIG_INTENT_KEYWORDS.some(k => msg.includes(k));
}

function detectConfigFields(message) {
  const msg = message.toLowerCase();
  const wantsProvider = /provider|provider.*(url|endpoint|api|management)|management.*provider/.test(msg);
  const wantsConsumer = /consumer|consumer.*(url|endpoint|api|management)|management.*consumer/.test(msg);
  const wantsApiKey = /api.?key|apikey/.test(msg);
  if (!wantsProvider && !wantsConsumer && !wantsApiKey) {
    return { showProvider: true, showConsumer: true, showApiKey: true };
  }
  return {
    showProvider: wantsProvider,
    showConsumer: wantsConsumer,
    showApiKey: wantsApiKey || wantsProvider || wantsConsumer,
  };
}

export default function Chatbot() {
  const theme = useStore(s => s.theme);
  const user = useStore(s => s.user);
  const isLight = theme === 'light';
  const navigate = useNavigate();

  const userRoles = user?.roles || [];
  const isProvider = userRoles.includes('data-provider') || userRoles.includes('admin') || userRoles.includes('realm-admin');
  const isConsumer = userRoles.includes('data-consumer') || userRoles.includes('admin') || userRoles.includes('realm-admin');

  const welcomeMessage = getWelcomeMessage(isProvider, isConsumer);
  const suggestions = getSuggestions(isProvider, isConsumer);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: welcomeMessage }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [open, messages]);

  const handleConfigSave = ({ providerUrl, consumerUrl, apiKey }) => {
    const entries = {};
    if (providerUrl) {
      entries.provider_url = providerUrl;
      entries.providerEdcUrl = providerUrl;
      entries.assetsProviderUrl = providerUrl;
      entries.policiesProviderUrl = providerUrl;
      if (apiKey) {
        entries.edc_api_key = apiKey;
        entries.providerApiKey = apiKey;
        entries.assetsApiKey = apiKey;
        entries.policiesApiKey = apiKey;
      }
    }
    if (consumerUrl) {
      entries.consumer_url = consumerUrl;
      entries.consumerEdcUrl = consumerUrl;
      if (apiKey) entries.consumerApiKey = apiKey;
    }
    saveConnectorConfigs(entries);
    const actions = [];
    if (providerUrl && isProvider) actions.push({ label: 'Open Assets', path: '/assets' });
    if (consumerUrl && isConsumer) actions.push({ label: 'Open Consumer Dashboard', path: '/consumer-dashboard' });
    if (!providerUrl && !consumerUrl) actions.push({ label: 'Open Dashboard', path: '/dashboard' });
    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Configuration saved! Your connector${providerUrl && consumerUrl ? 's are' : ' is'} now set up. Where would you like to go?`,
        actions,
      }]);
    }, 300);
  };

  const send = async (text) => {
    const content = text || input.trim();
    if (!content || loading) return;
    setInput('');

    const userMsg = { role: 'user', content };
    setMessages(prev => [...prev, userMsg]);

    // Config intent — show inline form with only relevant fields
    if (isConfigIntent(content)) {
      const fields = detectConfigFields(content);
      setMessages(prev => [...prev, { role: 'config-form', ...fields }]);
      return;
    }

    const newMessages = [...messages, userMsg];
    const pageActions = getPageActions(content, isProvider, isConsumer);
    setLoading(true);

    try {
      const authToken = localStorage.getItem('kc_token');
      const response = await fetch('https://enact-dataspace.iti.gr/api/v1/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ messages: newMessages, userRoles }),
      });

      if (!response.ok) throw new Error('Stream request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let firstToken = true;
      let pendingProposal = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.action_proposal) {
              pendingProposal = parsed.action_proposal;
            }
            if (parsed.token) {
              assistantContent += parsed.token;
              if (firstToken) {
                setLoading(false);
                firstToken = false;
                setMessages(prev => [...prev, { role: 'assistant', content: assistantContent }]);
              } else {
                setMessages(prev => {
                  const msgs = [...prev];
                  msgs[msgs.length - 1] = { role: 'assistant', content: assistantContent };
                  return msgs;
                });
              }
            }
          } catch {}
        }
      }

      // Attach page action buttons and any proposed create-action after the
      // response is complete (both arrive as part of the same stream, but
      // only make sense once the assistant's message text is finalized).
      if (pageActions.length > 0 || pendingProposal) {
        setMessages(prev => {
          const msgs = [...prev];
          msgs[msgs.length - 1] = {
            ...msgs[msgs.length - 1],
            ...(pageActions.length > 0 ? { actions: pageActions } : {}),
            ...(pendingProposal ? { actionProposal: pendingProposal } : {}),
          };
          return msgs;
        });
      }
    } catch {
      setLoading(false);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const setMessageAt = (index, patch) => {
    setMessages(prev => {
      const msgs = [...prev];
      msgs[index] = { ...msgs[index], ...patch };
      return msgs;
    });
  };

  const confirmAction = async (index) => {
    const proposal = messages[index]?.actionProposal;
    if (!proposal) return;
    setMessageAt(index, { actionStatus: 'pending' });
    try {
      const authToken = localStorage.getItem('kc_token');
      const response = await fetch('https://enact-dataspace.iti.gr/api/v1/chat/execute-action', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ action: proposal.action, arguments: proposal.arguments }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to execute action');
      }
      setMessageAt(index, { actionStatus: 'done' });
    } catch (err) {
      setMessageAt(index, { actionStatus: 'error', actionError: err.message });
    }
  };

  const cancelAction = (index) => {
    setMessageAt(index, { actionStatus: 'cancelled' });
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-20 right-6 z-50 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 ${isLight ? 'bg-blue-600 hover:bg-blue-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
        style={{ width: '52px', height: '52px' }}
        title="ENACT Assistant"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div className={`fixed bottom-[148px] right-6 left-6 sm:left-auto z-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden border transition-colors duration-200 ${
          isLight ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'
        }`} style={{ width: '520px', maxWidth: 'calc(100vw - 2rem)', height: '600px', maxHeight: 'calc(100vh - 180px)' }}>

          {/* Header */}
          <div className={`px-4 py-3 flex items-center gap-2 flex-shrink-0 ${isLight ? 'bg-blue-600' : 'bg-emerald-700'}`}>
            <Bot size={18} className="text-white opacity-80" />
            <div>
              <p className="text-sm font-semibold text-white">ENACT Assistant</p>
              <p className={`text-xs ${isLight ? 'text-blue-100' : 'text-emerald-200'}`}>Dataspace guide</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              msg.role === 'config-form' ? (
                <div key={i} className="flex gap-2">
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isLight ? 'bg-blue-500' : 'bg-emerald-600'}`}>
                    <Bot size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <ConfigFormMessage
                      isLight={isLight}
                      showProvider={msg.showProvider ?? isProvider}
                      showConsumer={msg.showConsumer ?? isConsumer}
                      showApiKey={msg.showApiKey ?? true}
                      onSave={handleConfigSave}
                    />
                  </div>
                </div>
              ) : (
                <Message key={i} msg={msg} index={i} isLight={isLight} onNavigate={handleNavigate}
                  onConfirmAction={confirmAction} onCancelAction={cancelAction} />
              )
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-blue-500' : 'bg-emerald-600'}`}>
                  <Bot size={14} className="text-white" />
                </div>
                <div className={`rounded-xl rounded-tl-none px-3 py-2 ${isLight ? 'bg-gray-200' : 'bg-gray-700'}`}>
                  <Loader2 size={16} className={`animate-spin ${isLight ? 'text-gray-500' : 'text-gray-300'}`} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions (only at start) */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1 flex-shrink-0">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className={`text-xs px-2 py-1 rounded-lg border transition-colors text-left ${
                    isLight
                      ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
                      : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className={`p-3 border-t flex gap-2 flex-shrink-0 ${isLight ? 'border-gray-300' : 'border-gray-700'}`}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask me anything..."
              rows={1}
              className={`flex-1 text-sm rounded-xl px-3 py-2 resize-none outline-none border transition-colors ${
                isLight
                  ? 'bg-gray-100 text-gray-900 border-gray-300 placeholder-gray-400 focus:border-blue-500'
                  : 'bg-gray-800 text-gray-100 border-gray-600 placeholder-gray-500 focus:border-emerald-500'
              }`}
              style={{ maxHeight: '80px' }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className={`rounded-xl px-3 flex items-center justify-center transition-colors ${
                input.trim() && !loading
                  ? isLight ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : isLight ? 'bg-gray-200 text-gray-400' : 'bg-gray-700 text-gray-500'
              }`}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
